import { Row } from "@tanstack/react-table";
import React, { useCallback, useMemo, useState } from "react";

import { DriveGridView } from "./DriveGridView";
import { DriveTableView } from "./DriveTableView";
import DriveUploadManager from "./DriveUploadManager";
import {
  filterDriveData,
  getActionOptions as getActionOptionsUtil,
} from "./DriveUtils";
import RenameItem from "./RenameItem";
import { ViewType } from "./ViewToggle";
import { Breadcrumb } from "./components/Breadcrumb";

import { driveTypes } from "@/constants/constant-values/default-values.constant";
import { DriveProvider, useDrive } from "@/context/DriveContext";
import { useDocumentManager } from "@/hooks/useDocumentManager";
import useDriveCopy, { CopyOptions } from "@/hooks/useDriveCopy";
import { useDriveMove } from "@/hooks/useDriveMove";
import { PaginationMeta, ResponsePagination } from "@/hooks/usePagination";
import { QueryIdReturnProps } from "@/hooks/useQueryId";
import { DirPath, IDrive } from "@/interfaces/project-register.interface";

// Content component that renders the appropriate view and modals
const DriveContent: React.FC = () => {
  const { viewType } = useDrive();

  return <>{viewType === "grid" ? <DriveGridView /> : <DriveTableView />}</>;
};

interface MainDriveProps {
  data: IDrive[];
  projectId: string;
  leadsId: string;
  projectName?: string;
  clientName?: string;
  setAddFolder?: React.Dispatch<React.SetStateAction<boolean>>;
  folderQuery: QueryIdReturnProps;
  previousDir: string | null | undefined;
  type: string;
  isManager?: boolean;
  viewType: ViewType;
  setViewType?: React.Dispatch<React.SetStateAction<ViewType>>;
  pagination: ResponsePagination;
  setExternalLocalPagination: React.Dispatch<
    React.SetStateAction<PaginationMeta>
  >;
  directoryPath: DirPath[];
}

export const MainDrive: React.FC<MainDriveProps> = ({
  data,
  projectId,
  leadsId,
  projectName,
  clientName,
  setAddFolder,
  folderQuery,
  previousDir,
  type,
  isManager = false,
  viewType,
  setViewType,
  pagination,
  setExternalLocalPagination,
  directoryPath,
}) => {
  const { id: folderId, set, reset } = folderQuery;

  const ID =
    (type === driveTypes.PROJECT_REGISTER ? leadsId : projectId) || "-id";

  const handleBreadcrumbNavigate = useCallback(
    (folderId: string | null) => {
      if (folderId === null) {
        reset();
      } else {
        set(folderId);
      }
    },
    [set, reset],
  );

  const [renameItemData, setRenameItemData] = useState<{
    isOpen: boolean;
    itemId: string;
    currentName: string;
    itemType: "dir" | "document";
  }>({
    isOpen: false,
    itemId: "",
    currentName: "",
    itemType: "dir",
  });

  const handleRenameItem = useCallback((item: IDrive) => {
    setRenameItemData({
      isOpen: true,
      itemId: item.id,
      currentName: item.name,
      itemType: item.type as "dir" | "document",
    });
  }, []);

  const toggleRenameModal = useCallback((state: boolean) => {
    setRenameItemData((prev) => ({ ...prev, isOpen: state }));
  }, []);

  // Get hooks for operations
  const { handleDelete } = useDocumentManager({ type });
  const {
    copyToTaskAndClaims: originalCopyToTaskAndClaims,
    copyToProject: originalCopyToProject,
    copyToProjectRegister: originalCopyToProjectRegister,
  } = useDriveCopy();

  const { moveDriveItem, cancelMove } = useDriveMove();

  // Create void-returning wrapper functions to fix type issues
  const copyToTaskAndClaims = useCallback(
    async (options: CopyOptions): Promise<void> => {
      await originalCopyToTaskAndClaims(options);
      // Return void explicitly
    },
    [originalCopyToTaskAndClaims],
  );

  const copyToProject = useCallback(
    async (options: CopyOptions): Promise<void> => {
      await originalCopyToProject(options);
      // Return void explicitly
    },
    [originalCopyToProject],
  );

  const copyToProjectRegister = useCallback(
    async (options: CopyOptions): Promise<void> => {
      await originalCopyToProjectRegister(options);
      // Return void explicitly
    },
    [originalCopyToProjectRegister],
  );

  // Handle folder addition
  const handleAddFolderClick = useCallback((): void => {
    if (setAddFolder) {
      setAddFolder((prev) => !prev);
    }
  }, [setAddFolder]);

  // Filter data based on current folder
  const filteredData = useMemo(() => {
    return filterDriveData(data, folderId);
  }, [data, folderId]);

  // Create action options for items
  const getActionOptions = useCallback(
    (row: Row<IDrive>): ActionButtonDataTypes[] => {
      return getActionOptionsUtil({
        row,
        folderId,
        isManager,
        type,
        handleDelete,
        handleRename: handleRenameItem,
        copyToTaskAndClaims,
        copyToProject,
        copyToProjectRegister,
        projectId,
        leadsId,
        previousDir,
        moveDriveItem,
        ID,
      });
    },
    [
      folderId,
      isManager,
      type,
      handleDelete,
      handleRenameItem,
      copyToTaskAndClaims,
      copyToProject,
      copyToProjectRegister,
      projectId,
      leadsId,
      previousDir,
      moveDriveItem,
      ID,
    ],
  );

  return (
    <DriveProvider
      initialData={{
        data: filteredData,
        folderId,
        previousDir,
        set,
        reset,
        getActionOptions,
        pagination,
        setExternalLocalPagination,
        viewType,
        // eslint-disable-next-line no-empty-function
        setViewType: setViewType || ((): void => {}),
        moveDriveItem,
        cancelMove,
        type,
        ID,
        projectName,
        clientName,
        directoryPath,
      }}
    >
      <DriveUploadManager
        onFolderAdd={setAddFolder ? handleAddFolderClick : undefined}
        isManager={isManager}
      >
        {directoryPath && directoryPath.length > 0 && (
          <div className='mb-4 p-3 bg-gray-50 rounded-lg border'>
            <Breadcrumb
              directoryPath={directoryPath}
              onNavigate={handleBreadcrumbNavigate}
            />
          </div>
        )}
        <DriveContent />
        {renameItemData.isOpen && (
          <RenameItem
            isOpen={renameItemData.isOpen}
            toggleModal={toggleRenameModal}
            folderFor={type}
            itemId={renameItemData.itemId}
            currentName={renameItemData.currentName}
            itemType={renameItemData.itemType}
          />
        )}
      </DriveUploadManager>
    </DriveProvider>
  );
};

export default MainDrive;
